import React, { useState } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Input,
    Select,
    Button,
    DatePicker,
    Tag,
    Popconfirm,
    message,
    Tooltip
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    ClearOutlined,
    SyncOutlined,
    CodeOutlined,
    BugOutlined,
    FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface SystemLogDto {
    id: string;
    timestamp: string;
    level: string;
    renderedMessage: string;
    exception?: string;
    properties?: string;
}

interface PaginatedLogsDto {
    items: SystemLogDto[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
}

export const LogsList: React.FC = () => {
    const queryClient = useQueryClient();
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [level, setLevel] = useState<string | undefined>(undefined);
    const [search, setSearch] = useState<string>('');
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const fetchLogs = async () => {
        const params = new URLSearchParams({
            pageIndex: pageIndex.toString(),
            pageSize: pageSize.toString(),
            ...(level && { level }),
            ...(search && { search }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate })
        });
        const response = await apiClient.get<{ data: PaginatedLogsDto }>(`/admin/logs?${params}`);
        return response.data.data;
    };

    const { data, isLoading, refetch } = useQuery<PaginatedLogsDto>({
        queryKey: ['adminLogs', pageIndex, pageSize, level, search, startDate, endDate],
        queryFn: fetchLogs
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            await apiClient.delete('/admin/logs', { data: { ids } });
        },
        onSuccess: () => {
            message.success('Logs deleted successfully');
            setSelectedRowKeys([]);
            queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
        },
        onError: () => message.error('Failed to delete logs')
    });

    const clearAllMutation = useMutation({
        mutationFn: async () => {
            await apiClient.delete('/admin/logs/clear');
        },
        onSuccess: () => {
            message.success('All logs cleared successfully');
            setSelectedRowKeys([]);
            setPageIndex(1);
            queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
        },
    });

    const deleteFilteredMutation = useMutation({
        mutationFn: async () => {
            const params = new URLSearchParams({
                ...(level && { level }),
                ...(search && { search }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate })
            });
            const response = await apiClient.delete<{ data: number }>(`/admin/logs/filtered?${params}`);
            return response.data.data;
        },
        onSuccess: (deletedCount) => {
            message.success(
                deletedCount !== undefined && deletedCount > 0
                    ? `${deletedCount} adet filtrelenmiş log başarıyla silindi.`
                    : 'Filtrelenmiş loglar başarıyla silindi.'
            );
            setSelectedRowKeys([]);
            setPageIndex(1);
            queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
        },
        onError: () => message.error('Filtrelenmiş loglar silinirken bir hata oluştu.')
    });

    const hasActiveFilter = Boolean(level || search.trim() || startDate || endDate);

    const getFilterSummary = () => {
        const parts: string[] = [];
        if (level) parts.push(`Level: ${level}`);
        if (search.trim()) parts.push(`Arama: "${search.trim()}"`);
        if (startDate && endDate) {
            parts.push(`Tarih: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`);
        } else if (startDate) {
            parts.push(`Başlangıç: ${new Date(startDate).toLocaleDateString()}`);
        } else if (endDate) {
            parts.push(`Bitiş: ${new Date(endDate).toLocaleDateString()}`);
        }
        return parts.length > 0 ? parts.join(' | ') : 'Tüm Loglar';
    };

    const handleDeleteSelected = () => {
        if (selectedRowKeys.length > 0) {
            deleteMutation.mutate(selectedRowKeys as string[]);
        }
    };

    const handleClearAll = () => {
        clearAllMutation.mutate();
    };

    const getLevelColor = (lvl: string) => {
        switch (lvl) {
            case 'Fatal':
            case 'Error': return 'red';
            case 'Warning': return 'orange';
            case 'Information': return 'blue';
            case 'Debug': return 'green';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString()
        },
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            width: 100,
            render: (lvl: string) => <Tag color={getLevelColor(lvl)}>{lvl}</Tag>
        },
        {
            title: 'Message',
            dataIndex: 'renderedMessage',
            key: 'renderedMessage',
            render: (msg: string) => <Text ellipsis={{ tooltip: msg }} style={{ maxWidth: 400 }}>{msg}</Text>
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_: any, record: SystemLogDto) => (
                <Popconfirm
                    title="Delete this log?"
                    onConfirm={() => deleteMutation.mutate([record.id])}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    const expandedRowRender = (record: SystemLogDto) => {
        let parsedProps = null;
        if (record.properties) {
            try {
                parsedProps = JSON.parse(record.properties);
            } catch {
                parsedProps = record.properties;
            }
        }

        return (
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                {record.exception && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ color: 'red' }}><BugOutlined /> Exception:</Text>
                        <pre style={{ background: '#fff', padding: 12, borderRadius: 4, overflowX: 'auto', fontSize: '12px' }}>
                            {record.exception}
                        </pre>
                    </div>
                )}
                {parsedProps && (
                    <div>
                        <Text strong><CodeOutlined /> Properties:</Text>
                        <pre style={{ background: '#fff', padding: 12, borderRadius: 4, overflowX: 'auto', fontSize: '12px' }}>
                            {JSON.stringify(parsedProps, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card
            title={<Title level={4}>System Logs</Title>}
            extra={
                <Space>
                    <Button icon={<SyncOutlined />} onClick={() => refetch()}>Refresh</Button>
                    <Popconfirm
                        title="Are you sure you want to clear ALL logs?"
                        onConfirm={handleClearAll}
                        okText="Yes, Clear All"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button danger icon={<ClearOutlined />}>Clear All</Button>
                    </Popconfirm>
                </Space>
            }
        >
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <Input
                    placeholder="Search messages or exceptions..."
                    allowClear
                    prefix={<SearchOutlined />}
                    onPressEnter={(e) => setSearch(e.currentTarget.value)}
                    onBlur={(e) => setSearch(e.target.value)}
                    style={{ width: 250 }}
                />
                <Select
                    allowClear
                    placeholder="Filter by Level"
                    style={{ width: 150 }}
                    onChange={(val) => setLevel(val)}
                    options={[
                        { value: 'Error', label: 'Error' },
                        { value: 'Warning', label: 'Warning' },
                        { value: 'Information', label: 'Information' },
                        { value: 'Debug', label: 'Debug' },
                        { value: 'Fatal', label: 'Fatal' },
                    ]}
                />
                <RangePicker
                    showTime
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                            setStartDate(dates[0].toISOString());
                            setEndDate(dates[1].toISOString());
                        } else {
                            setStartDate(undefined);
                            setEndDate(undefined);
                        }
                    }}
                />

                <Tooltip title={!hasActiveFilter ? 'Filtreye göre silmek için önce en az bir filtre uygulayın (örn. Level: Information)' : undefined}>
                    <span>
                        <Popconfirm
                            title="Filtrelenmiş Logları Sil"
                            description={
                                <div style={{ maxWidth: 320 }}>
                                    <p style={{ marginBottom: 6 }}>
                                        Şu anki filtrelere uyan <strong>tüm loglar</strong> kalıcı olarak silinecektir:
                                    </p>
                                    <div style={{ background: '#f5f5f5', padding: '6px 10px', borderRadius: 4, fontSize: '12px' }}>
                                        <div><strong>Filtre:</strong> {getFilterSummary()}</div>
                                        <div><strong>Eşleşen Kayıt:</strong> {data?.totalCount ?? 0}</div>
                                    </div>
                                </div>
                            }
                            onConfirm={() => deleteFilteredMutation.mutate()}
                            okText="Evet, Filtrelenenleri Sil"
                            cancelText="Vazgeç"
                            okButtonProps={{ danger: true, loading: deleteFilteredMutation.isPending }}
                            disabled={!hasActiveFilter || !data || data.totalCount === 0 || deleteFilteredMutation.isPending}
                        >
                            <Button
                                danger
                                icon={<FilterOutlined />}
                                loading={deleteFilteredMutation.isPending}
                                disabled={!hasActiveFilter || !data || data.totalCount === 0}
                            >
                                Filtrelenmişleri Sil {hasActiveFilter && data?.totalCount !== undefined ? `(${data.totalCount})` : ''}
                            </Button>
                        </Popconfirm>
                    </span>
                </Tooltip>

                {selectedRowKeys.length > 0 && (
                    <Popconfirm title={`Delete ${selectedRowKeys.length} selected logs?`} onConfirm={handleDeleteSelected}>
                        <Button danger icon={<DeleteOutlined />}>
                            Delete Selected ({selectedRowKeys.length})
                        </Button>
                    </Popconfirm>
                )}
            </div>

            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys)
                }}
                columns={columns}
                dataSource={data?.items}
                rowKey="id"
                loading={isLoading || deleteMutation.isPending || clearAllMutation.isPending || deleteFilteredMutation.isPending}
                expandable={{ expandedRowRender }}
                pagination={{
                    current: pageIndex,
                    pageSize: pageSize,
                    total: data?.totalCount || 0,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                    }
                }}
            />
        </Card>
    );
};
